import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { Alert } from "../../components/Alert";
import { RequirePermission } from "../../components/crm/RequirePermission";
import { StatusBadge } from "../../components/crm/StatusBadge";
import { Tabs, type TabDefinition } from "../../components/crm/Tabs";
import { Spinner } from "../../components/Spinner";
import {
  completeActivity,
  getCompany,
  isApiError,
  listActivities,
  listContacts,
  listDeals,
} from "../../lib/crm-api";
import type { Activity, Company, Contact, Deal } from "../../lib/crm-types";
import type { TranslationKey } from "../../lib/i18n";
import { formatDate, formatMoney } from "../../lib/format";
import { useConfig } from "../../state/config-context";
import { useSession } from "../../state/session-context";

const ACTIVITY_KIND_KEYS: Record<string, TranslationKey> = {
  call: "crm.activity.kind.call",
  email: "crm.activity.kind.email",
  meeting: "crm.activity.kind.meeting",
  note: "crm.activity.kind.note",
  task: "crm.activity.kind.task",
};

const TABS: TabDefinition[] = [
  { id: "summary", labelKey: "crm.company.tabs.summary" },
  { id: "contacts", labelKey: "crm.company.tabs.contacts" },
  { id: "deals", labelKey: "crm.company.tabs.deals" },
  { id: "activities", labelKey: "crm.company.tabs.activities" },
];

function Field({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <div className="detail-list__row">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function CompanyContactsTab({ companyId }: { companyId: string }): ReactNode {
  const { t, lang } = useConfig();
  const [items, setItems] = useState<Contact[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const locale = lang === "es" ? "es-ES" : "en-US";

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const page = await listContacts({ company_id: companyId, limit: 100 });
        if (!cancelled) {
          setItems(page.items);
        }
      } catch (caught) {
        if (!cancelled) {
          setError(isApiError(caught) ? caught.detail : t("error.network"));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, t]);

  if (error !== null) return <Alert tone="error">{error}</Alert>;
  if (items === null) return <Spinner label={t("app.loading")} />;
  if (items.length === 0) return <p className="empty">{t("crm.contacts.empty")}</p>;

  return (
    <div className="table-wrap">
      <table className="table">
        <caption className="sr-only">{t("crm.company.tabs.contacts")}</caption>
        <thead>
          <tr>
            <th scope="col">{t("crm.contact.firstName")}</th>
            <th scope="col">{t("crm.contact.email")}</th>
            <th scope="col">{t("crm.common.status")}</th>
            <th scope="col">{t("crm.common.updated")}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((contact) => (
            <tr key={contact.id}>
              <td>
                <Link to={`/crm/contacts/${contact.id}`}>
                  {[contact.first_name, contact.last_name].filter(Boolean).join(" ")}
                </Link>
              </td>
              <td>{contact.email ?? "—"}</td>
              <td>
                <StatusBadge status={contact.status} />
              </td>
              <td>{formatDate(contact.updated_at, locale)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CompanyDealsTab({ companyId }: { companyId: string }): ReactNode {
  const { t, lang } = useConfig();
  const [items, setItems] = useState<Deal[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const locale = lang === "es" ? "es-ES" : "en-US";

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const page = await listDeals({ company_id: companyId, limit: 100 });
        if (!cancelled) {
          setItems(page.items);
        }
      } catch (caught) {
        if (!cancelled) {
          setError(isApiError(caught) ? caught.detail : t("error.network"));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, t]);

  if (error !== null) return <Alert tone="error">{error}</Alert>;
  if (items === null) return <Spinner label={t("app.loading")} />;
  if (items.length === 0) return <p className="empty">{t("crm.deals.empty")}</p>;

  return (
    <div className="table-wrap">
      <table className="table">
        <caption className="sr-only">{t("crm.company.tabs.deals")}</caption>
        <thead>
          <tr>
            <th scope="col">{t("crm.deal.title")}</th>
            <th scope="col">{t("crm.deal.value")}</th>
            <th scope="col">{t("crm.common.status")}</th>
            <th scope="col">{t("crm.deal.expectedClose")}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((deal) => (
            <tr key={deal.id}>
              <td>{deal.title}</td>
              <td>{formatMoney(deal.value_minor, deal.currency, locale)}</td>
              <td>
                <StatusBadge status={deal.status} />
              </td>
              <td>{formatDate(deal.expected_close_date, locale)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CompanyActivitiesTab({ companyId }: { companyId: string }): ReactNode {
  const { t, lang } = useConfig();
  const { hasPermission } = useSession();
  const canUpdate = hasPermission("crm.activity.update");
  const [items, setItems] = useState<Activity[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const locale = lang === "es" ? "es-ES" : "en-US";

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const page = await listActivities({
          related_type: "company",
          related_id: companyId,
          limit: 100,
        });
        if (!cancelled) {
          setItems(page.items);
        }
      } catch (caught) {
        if (!cancelled) {
          setError(isApiError(caught) ? caught.detail : t("error.network"));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, t]);

  const onComplete = async (activityId: string): Promise<void> => {
    setBusyId(activityId);
    setError(null);
    try {
      const updated = await completeActivity(activityId);
      setItems((current) =>
        current === null ? current : current.map((row) => (row.id === updated.id ? updated : row)),
      );
    } catch (caught) {
      setError(isApiError(caught) ? caught.detail : t("error.network"));
    } finally {
      setBusyId(null);
    }
  };

  if (error !== null) return <Alert tone="error">{error}</Alert>;
  if (items === null) return <Spinner label={t("app.loading")} />;
  if (items.length === 0) return <p className="empty">{t("crm.activities.empty")}</p>;

  return (
    <div className="table-wrap">
      <table className="table">
        <caption className="sr-only">{t("crm.company.tabs.activities")}</caption>
        <thead>
          <tr>
            <th scope="col">{t("crm.activity.subject")}</th>
            <th scope="col">{t("crm.activity.kind")}</th>
            <th scope="col">{t("crm.common.status")}</th>
            <th scope="col">{t("crm.activity.dueAt")}</th>
            <th scope="col" className="table__actions-col">
              <span className="sr-only">{t("crm.activity.complete")}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((activity) => (
            <tr key={activity.id}>
              <td>{activity.subject}</td>
              <td>
                {ACTIVITY_KIND_KEYS[activity.kind] === undefined
                  ? activity.kind
                  : t(ACTIVITY_KIND_KEYS[activity.kind] as TranslationKey)}
              </td>
              <td>
                <StatusBadge status={activity.status} />
              </td>
              <td>{formatDate(activity.due_at, locale)}</td>
              <td className="table__actions-col">
                {canUpdate && activity.status === "pending" ? (
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    disabled={busyId === activity.id}
                    onClick={() => void onComplete(activity.id)}
                  >
                    {busyId === activity.id ? t("common.saving") : t("crm.activity.complete")}
                  </button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CompanyDetailInner({ id }: { id: string }): ReactNode {
  const { t } = useConfig();
  const { hasPermission } = useSession();
  const canUpdate = hasPermission("crm.company.update");
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState("summary");

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      setCompany(await getCompany(id));
    } catch (caught) {
      setError(isApiError(caught) ? caught.detail : t("error.network"));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className="page" aria-labelledby="crm-company-detail-title">
      <header className="page__header page__header--row">
        <div>
          <h1 id="crm-company-detail-title">{company?.name ?? t("crm.companies.detail")}</h1>
          {company !== null ? (
            <p className="muted">
              <StatusBadge status={company.status} />
            </p>
          ) : null}
        </div>
        {canUpdate && company !== null ? (
          <Link className="btn btn--ghost" to={`/crm/companies/${company.id}/edit`}>
            {t("crm.common.edit")}
          </Link>
        ) : null}
      </header>

      {error !== null ? <Alert tone="error">{error}</Alert> : null}
      {loading ? <Spinner label={t("app.loading")} /> : null}

      {!loading && company !== null ? (
        <>
          <Tabs tabs={TABS} active={tab} onChange={setTab} />
          <div role="tabpanel" id={`panel-${tab}`} aria-labelledby={`tab-${tab}`}>
            {tab === "summary" ? (
              <dl className="detail-list">
                <Field label={t("crm.company.name")}>{company.name}</Field>
                <Field label={t("crm.company.legalName")}>{company.legal_name ?? "—"}</Field>
                <Field label={t("crm.company.taxId")}>{company.tax_id ?? "—"}</Field>
                <Field label={t("crm.company.email")}>{company.email ?? "—"}</Field>
                <Field label={t("crm.common.status")}>
                  <StatusBadge status={company.status} />
                </Field>
                <Field label={t("crm.company.tags")}>
                  {company.tags.length > 0 ? (
                    <ul className="tag-list">
                      {company.tags.map((tag) => (
                        <li key={tag} className="tag">
                          {tag}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    "—"
                  )}
                </Field>
              </dl>
            ) : null}
            {tab === "contacts" ? <CompanyContactsTab companyId={company.id} /> : null}
            {tab === "deals" ? <CompanyDealsTab companyId={company.id} /> : null}
            {tab === "activities" ? <CompanyActivitiesTab companyId={company.id} /> : null}
          </div>
        </>
      ) : null}
    </section>
  );
}

export function CompanyDetailPage(): ReactNode {
  const { id } = useParams<{ id: string }>();
  if (id === undefined) {
    return null;
  }
  return (
    <RequirePermission permission="crm.company.read">
      <CompanyDetailInner id={id} />
    </RequirePermission>
  );
}
